import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Info } from "lucide-react";

type Op = "gt" | "gte" | "lt" | "lte" | "eq" | "neq" | "in" | "contains";

export type ClauseConditionExpr =
  | { kind: "always" }
  | { kind: "never" }
  | { kind: "field_op"; field: string; op: Op; value: string | number | string[] }
  | { kind: "and"; exprs: ClauseConditionExpr[] }
  | { kind: "or"; exprs: ClauseConditionExpr[] };

const CONDITION_FIELDS = [
  { value: "compensation.base_salary", label: "Annual Salary", type: "number" },
  { value: "term.years", label: "Term Length (years)", type: "number" },
  { value: "contract.type", label: "Contract Type", type: "enum", options: [
    { value: "head_coach", label: "Head Coach" },
    { value: "assistant_coach", label: "Assistant Coach" },
    { value: "coordinator", label: "Coordinator" },
    { value: "interim", label: "Interim" },
  ]},
  { value: "coach.role", label: "Coach Role", type: "enum", options: [
    { value: "head_coach", label: "Head Coach" },
    { value: "assistant_coach", label: "Assistant Coach" },
    { value: "coordinator", label: "Coordinator" },
    { value: "interim", label: "Interim" },
  ]},
  { value: "coach.sport", label: "Sport", type: "string" },
  { value: "institution.type", label: "Institution Division", type: "enum", options: [
    { value: "D1", label: "NCAA Division I" },
    { value: "D2", label: "NCAA Division II" },
    { value: "D3", label: "NCAA Division III" },
    { value: "NAIA", label: "NAIA" },
    { value: "JUCO", label: "Junior College" },
    { value: "other", label: "Other" },
  ]},
];

const NUMERIC_OPS: { value: Op; label: string }[] = [
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
  { value: "eq", label: "=" },
  { value: "neq", label: "≠" },
];

const STRING_OPS: { value: Op; label: string }[] = [
  { value: "eq", label: "equals" },
  { value: "neq", label: "not equals" },
  { value: "contains", label: "contains" },
  { value: "in", label: "is one of" },
];

function FieldOpForm({
  field,
  op,
  value,
  onChange,
}: {
  field: string;
  op: Op;
  value: string | number | string[];
  onChange: (field: string, op: Op, value: string | number | string[]) => void;
}) {
  const fieldMeta = CONDITION_FIELDS.find((f) => f.value === field);
  const isNumeric = fieldMeta?.type === "number";
  const isEnum = fieldMeta?.type === "enum";
  const ops = isNumeric ? NUMERIC_OPS : STRING_OPS;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="space-y-1 min-w-[160px]">
        <Label className="text-xs">Field</Label>
        <Select value={field} onValueChange={(v) => onChange(v, op, value)}>
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONDITION_FIELDS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 min-w-[90px]">
        <Label className="text-xs">Operator</Label>
        <Select value={op} onValueChange={(v) => onChange(field, v as Op, value)}>
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ops.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1 min-w-[120px]">
        <Label className="text-xs">Value</Label>
        {isEnum && fieldMeta?.options ? (
          <Select
            value={String(value)}
            onValueChange={(v) => onChange(field, op, v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldMeta.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : isNumeric ? (
          <Input
            type="number"
            className="h-8 text-sm"
            value={typeof value === "number" ? value : ""}
            onChange={(e) => onChange(field, op, parseFloat(e.target.value) || 0)}
            placeholder="e.g. 250000"
          />
        ) : (
          <Input
            className="h-8 text-sm"
            value={op === "in" ? (Array.isArray(value) ? value.join(", ") : String(value)) : String(value)}
            onChange={(e) => {
              if (op === "in") {
                onChange(field, op, e.target.value.split(",").map((v) => v.trim()).filter(Boolean));
              } else {
                onChange(field, op, e.target.value);
              }
            }}
            placeholder={op === "in" ? "val1, val2, val3" : "value"}
          />
        )}
      </div>
    </div>
  );
}

function ExpressionNode({
  expr,
  onChange,
  onRemove,
  depth = 0,
}: {
  expr: ClauseConditionExpr;
  onChange: (e: ClauseConditionExpr) => void;
  onRemove?: () => void;
  depth?: number;
}) {
  if (expr.kind === "always") {
    return (
      <div className="flex items-center gap-2 p-2 rounded-md border bg-emerald-50 border-emerald-200">
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Always include</Badge>
        {onRemove && (
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={onRemove}>
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  if (expr.kind === "field_op") {
    return (
      <div className="flex items-start gap-2 p-2 rounded-md border bg-muted/30">
        <FieldOpForm
          field={expr.field}
          op={expr.op}
          value={expr.value}
          onChange={(f, o, v) => onChange({ kind: "field_op", field: f, op: o, value: v })}
        />
        {onRemove && (
          <Button variant="ghost" size="icon" className="h-8 w-8 mt-5 shrink-0" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        )}
      </div>
    );
  }

  if (expr.kind === "and" || expr.kind === "or") {
    const isAnd = expr.kind === "and";
    return (
      <div className={`rounded-md border p-3 space-y-2 ${depth > 0 ? "bg-muted/20" : "bg-background"}`}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Match</span>
          <Select
            value={expr.kind}
            onValueChange={(v) => onChange({ kind: v as "and" | "or", exprs: expr.exprs })}
          >
            <SelectTrigger className="h-7 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">ALL of</SelectItem>
              <SelectItem value="or">ANY of</SelectItem>
            </SelectContent>
          </Select>
          {onRemove && (
            <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={onRemove}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        <div className="space-y-2 pl-3 border-l-2 border-muted">
          {expr.exprs.map((child, i) => (
            <ExpressionNode
              key={i}
              expr={child}
              depth={depth + 1}
              onChange={(updated) => {
                const newExprs = [...expr.exprs];
                newExprs[i] = updated;
                onChange({ kind: expr.kind, exprs: newExprs });
              }}
              onRemove={expr.exprs.length > 1 ? () => {
                const newExprs = expr.exprs.filter((_, j) => j !== i);
                onChange({ kind: expr.kind, exprs: newExprs });
              } : undefined}
            />
          ))}
        </div>
        <div className="flex gap-2 pl-3">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onChange({
              kind: expr.kind,
              exprs: [...expr.exprs, { kind: "field_op", field: "compensation.base_salary", op: "gt", value: 0 }],
            })}
          >
            <Plus className="h-3 w-3 mr-1" /> Add condition
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

interface ClauseConditionBuilderProps {
  condition: ClauseConditionExpr;
  required: boolean;
  onConditionChange: (c: ClauseConditionExpr) => void;
  onRequiredChange: (r: boolean) => void;
}

export function ClauseConditionBuilder({
  condition,
  required,
  onConditionChange,
  onRequiredChange,
}: ClauseConditionBuilderProps) {
  const [enabled, setEnabled] = useState(condition.kind !== "always");

  // Sync enabled when condition prop changes asynchronously (e.g. clause data loads after mount)
  useEffect(() => {
    setEnabled(condition.kind !== "always");
  }, [condition.kind]);

  const handleToggle = (on: boolean) => {
    setEnabled(on);
    if (!on) {
      onConditionChange({ kind: "always" });
    } else {
      // Only default to a new expression if the current condition is "always"
      // (i.e. there's no existing condition to preserve)
      if (condition.kind === "always") {
        onConditionChange({ kind: "field_op", field: "compensation.base_salary", op: "gt", value: 250000 });
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="text-sm font-medium">Inclusion Condition</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Define when this clause should be suggested or required on a contract.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggle} />
      </div>

      {enabled && (
        <div className="space-y-3">
          <ExpressionNode expr={condition} onChange={onConditionChange} />
          <div className="flex gap-2">
            {condition.kind !== "and" && condition.kind !== "or" && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => onConditionChange({
                  kind: "and",
                  exprs: [
                    condition,
                    { kind: "field_op", field: "compensation.base_salary", op: "gt", value: 0 },
                  ],
                })}
              >
                <Plus className="h-3 w-3 mr-1" /> Add AND condition
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 rounded-md border p-3 bg-muted/20">
            <div className="flex-1">
              <Label className="text-sm font-medium">Required</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When enabled, contracts matching this condition <strong>must</strong> include this clause. Otherwise it's a suggestion.
              </p>
            </div>
            <Switch checked={required} onCheckedChange={onRequiredChange} />
          </div>
          <div className="flex items-start gap-2 p-2 rounded-md bg-blue-50 border border-blue-100 text-xs text-blue-700">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Conditions are evaluated in real time against each contract's data when the Compliance panel is loaded.</span>
          </div>
        </div>
      )}
    </div>
  );
}
